import { emptyCache } from "../localStorageData";
import { MajorGrade } from "../storage/classes/MajorGrade";
import type legacyStorageTypes from "../storage/legacyStorageTypes";
import type {
  ClassInfo,
  Grade,
  IdName,
  IdNameCountInterface,
  IdProxy,
  Level,
  Major,
  Module,
  ModuleGroup,
  ModuleTags,
  SchoolInfo,
  StaffInfo,
  StudentInfo,
  SubModuleGroup,
  Topic,
  TopicTags
} from "../storage/legacyStorageTypes";

const grades = new Map<number, Grade>();
const levels = new Map<number, Level>();
const majors = new Map<number, Major>();
const moduleGroups = new Map<number, ModuleGroup>();
const modules = new Map<number, Module>();
const topics = new Map<number, Topic>();
const staffInfos = new Map<number, StaffInfo>();
const schoolInfos = new Map<number, SchoolInfo>();
const classInfos = new Map<number, ClassInfo>();
const studentInfos = new Map<number, StudentInfo>();
const subModuleGroups = new Map<number, SubModuleGroup>();
const majorGradeClassInfo = new Map<string, number[]>();
const majorGradeStudentInfo = new Map<string, number[]>();
const majorGradeModule = new Map<string, number[]>();
const studentClassInfo = new Map<number, number[]>();
const CACHE_LOADED_EVENT = new Event("CACHE_LOADED_EVENT");
let ready = false;
type DoWhenReadyFunction = () => void;
const doWhenReadyFunctions: DoWhenReadyFunction[] = [];
export let rawCache: legacyStorageTypes = emptyCache;
const LoggedInGatekeeper = {
  hasUnlimitedAccessToModule: () => true,
  hasCreditBaseAccessToModule: () => false
};

function generateLevelCache() {
  if (rawCache.level) {
    rawCache.level.map((levelItem) => {
      levels.set(levelItem.id, levelItem);
    });
  }
}

function generateGradeCache() {
  if (rawCache.grade) {
    rawCache.grade.map((gradeItem) => {
      grades.set(gradeItem.id, gradeItem);
      const level = levels.get(gradeItem.levelId) as Level;
      if (level) {
        if (level.grades == undefined) level.grades = new Array<number>();
        if (!level.grades.includes(gradeItem.id))
          level.grades.push(gradeItem.id);
      }
    });
  }
}

function generateMajorCache() {
  if (rawCache.major) {
    rawCache.major.map((majorItem) => {
      majors.set(majorItem.id, majorItem);
    });
  }
}

function generateModuleGroupCache() {
  if (rawCache.moduleGroup) {
    rawCache.moduleGroup.map((moduleGroupsItem) => {
      moduleGroups.set(moduleGroupsItem.id, moduleGroupsItem);
    });
  }
}

function generateModuleCache() {
  if (rawCache.module) {
    rawCache.module.map((moduleItem) => {
      if (LoggedInGatekeeper.hasUnlimitedAccessToModule())
        moduleItem["isTimeLimit"] = true;
      else if (LoggedInGatekeeper.hasCreditBaseAccessToModule())
        moduleItem["isTimeLimit"] = false;

      modules.set(moduleItem.id, moduleItem);
    });

    if (rawCache.moduleTags == undefined || rawCache.moduleTags.length == 0) {
      return;
    }

    rawCache.moduleTags.map((moduleTagItem: ModuleTags) => {
      if (moduleTagItem.id == -1) {
        if (moduleTagItem.idNameCountList) {
          modules.forEach((module) => {
            moduleTagItem.idNameCountList.map(
              (tagItem: IdNameCountInterface) => {
                if (module.moduleTags == undefined)
                  module.moduleTags = new Array<IdNameCountInterface>();
                if (
                  module.moduleTags.filter(
                    (moduleTag) =>
                      moduleTag.id === tagItem.id &&
                      moduleTag.name === tagItem.name &&
                      moduleTag.count === tagItem.count
                  ).length === 0
                ) {
                  module.moduleTags.push(tagItem);
                }
              }
            );
          });
        }
      } else {
        const res = modules.get(moduleTagItem.id) as Module;
        if (res && moduleTagItem.idNameCountList) {
          moduleTagItem.idNameCountList.map((tagItem: IdNameCountInterface) => {
            if (res.moduleTags == undefined)
              res.moduleTags = new Array<IdNameCountInterface>();
            if (
              res.moduleTags.filter(
                (moduleTag) =>
                  moduleTag.id === tagItem.id &&
                  moduleTag.name === tagItem.name &&
                  moduleTag.count === tagItem.count
              ).length === 0
            ) {
              res.moduleTags.push(tagItem);
            }
          });
        }
      }
    });
  }
}

function generateTopicCache() {
  if (rawCache.topic) {
    rawCache.topic.map((topicItem) => {
      topics.set(topicItem.id, topicItem);

      if (topicItem.parentTopicId == undefined) {
        // this is a chapter, needs
        // to be added to modules'
        // chapters
        const res = modules.get(topicItem.moduleId) as Module;
        if (res) {
          if (res.chapters == undefined) res.chapters = new Array<number>();
          if (!res.chapters.includes(topicItem.id))
            res.chapters.push(topicItem.id);
        }
      }
    });
    if (rawCache.topicTags) {
      rawCache.topicTags.map((topicTagItem: TopicTags) => {
        if (topicTagItem.id == -1) {
          if (topicTagItem.idNameCountList) {
            topics.forEach((topic) => {
              topicTagItem.idNameCountList.map(
                (tagItem: IdNameCountInterface) => {
                  if (topic.topicTags == undefined)
                    topic.topicTags = new Array<IdNameCountInterface>();
                  if (
                    topic.topicTags.filter(
                      (topicTag) =>
                        topicTag.id === tagItem.id &&
                        topicTag.name === tagItem.name &&
                        topicTag.count === tagItem.count
                    ).length === 0
                  ) {
                    topic.topicTags.push(tagItem);
                  }
                }
              );
            });
          }
        } else {
          const topic = topics.get(topicTagItem.id) as Topic;
          if (topic && topicTagItem.idNameCountList) {
            topicTagItem.idNameCountList.map(
              (tagItem: IdNameCountInterface) => {
                if (topic.topicTags == undefined)
                  topic.topicTags = new Array<IdNameCountInterface>();
                if (
                  topic.topicTags.filter(
                    (topicTag) =>
                      topicTag.id === tagItem.id &&
                      topicTag.name === tagItem.name &&
                      topicTag.count === tagItem.count
                  ).length === 0
                ) {
                  topic.topicTags.push(tagItem);
                }
              }
            );
          }
        }
      });
    }
    addTopicChildren();
  }
}

function addTopicChildren() {
  topics.forEach((topic) => {
    if (topic.parentTopicId) {
      const parent = topics.get(topic.parentTopicId) as Topic;
      if (parent) {
        if (parent.children == undefined) parent.children = new Array<number>();
        if (!parent.children.includes(topic.id)) parent.children.push(topic.id);
      }
    }
  });
}

function generateStaffInfoCache() {
  // TODO: Fix response errors and problems
  if (rawCache.staffInfo) {
    rawCache.staffInfo.map((staffInfoItem) => {
      staffInfos.set(staffInfoItem.id, staffInfoItem);
    });
  }
}

function generateSchoolInfoCache() {
  if (rawCache.schoolInfo) {
    rawCache.schoolInfo.map((schoolInfoItem) => {
      schoolInfos.set(schoolInfoItem.id, schoolInfoItem);
    });
  }
}

function generateClassInfoCache() {
  if (rawCache.classInfo) {
    rawCache.classInfo.map((classInfoItem) => {
      classInfos.set(classInfoItem.id, classInfoItem);
    });
  }
}

function generateStudentInfoCache() {
  if (rawCache.studentInfo) {
    rawCache.studentInfo.map((studentInfoItem) => {
      studentInfos.set(studentInfoItem.id, studentInfoItem);
    });
    generateStudentClassCache();
  }
}

function generateStudentClassCache() {
  if (studentInfos && studentInfos.size > 0) {
    studentInfos.forEach((studentInfo) => {
      const majorGradeKey: string = new MajorGrade(
        studentInfo.majorId,
        studentInfo.grade
      ).getKeyOfMajorGrade();
      studentInfo.classIds?.map((classId: IdProxy) => {
        let classStudents = studentClassInfo.get(classId.id) as number[];
        if (classStudents == undefined) {
          classStudents = new Array<number>();
          studentClassInfo.set(classId.id, classStudents);
        }
        if (!classStudents.includes(studentInfo.id))
          classStudents.push(studentInfo.id);

        let majorGradeStudents = majorGradeStudentInfo.get(
          majorGradeKey
        ) as number[];
        if (majorGradeStudents == undefined) {
          majorGradeStudents = new Array<number>();
          majorGradeStudentInfo.set(majorGradeKey, majorGradeStudents);
        }
        if (!majorGradeStudents.includes(studentInfo.id))
          majorGradeStudents.push(studentInfo.id);

        let aMajorGradeClassInfo = majorGradeClassInfo.get(
          majorGradeKey
        ) as number[];
        if (aMajorGradeClassInfo == undefined) {
          aMajorGradeClassInfo = new Array<number>();
          majorGradeClassInfo.set(majorGradeKey, aMajorGradeClassInfo);
        }
        if (!aMajorGradeClassInfo.includes(classId.id))
          aMajorGradeClassInfo.push(classId.id);
      });
    });
  }
}

function generateMajorGradeModule() {
  if (rawCache.majorModuleGrade) {
    rawCache.majorModuleGrade.map((bean) => {
      const mg: MajorGrade = new MajorGrade(bean.majorId, bean.grade);
      updateMajorsInGrade(mg);
      let moduleList = majorGradeModule.get(
        mg.getKeyOfMajorGrade()
      ) as number[];
      if (moduleList == undefined) moduleList = new Array<number>();

      if (!moduleList.includes(bean.moduleId)) moduleList.push(bean.moduleId);

      majorGradeModule.set(mg.getKeyOfMajorGrade(), moduleList);
      const res = modules.get(bean.moduleId) as Module;
      if (res) {
        if (res.majorGrades == undefined) res.majorGrades = new Array<string>();

        if (!res.majorGrades.includes(mg.getKeyOfMajorGrade()))
          res.majorGrades.push(mg.getKeyOfMajorGrade());

        const moduleGroupId: number = res.moduleGroupId;
        const moduleGroup = moduleGroups.get(moduleGroupId) as ModuleGroup;
        if (res.orderInExam == undefined)
          res.orderInExam = new Map<string, number>();
        res.orderInExam.set(mg.getKeyOfMajorGrade(), bean.orderInExam ?? 0);

        if (moduleGroup) {
          if (moduleGroup.majorGrades == undefined)
            moduleGroup.majorGrades = new Array<string>();

          if (!moduleGroup.majorGrades.includes(mg.getKeyOfMajorGrade()))
            moduleGroup.majorGrades.push(mg.getKeyOfMajorGrade());
          moduleGroup.isCommon = res.isCommon;
        }
      }
    });
  }
}

function updateMajorsInGrade(mg: MajorGrade) {
  const grade = grades.get(mg.grade) as Grade;
  if (grade == undefined) return;

  if (grade.majors == undefined) grade.majors = new Array<number>();

  if (!grade.majors.includes(mg.majorId)) grade.majors.push(mg.majorId);
}

function generateSubModuleGroup() {
  if (rawCache.subModuleGroup) {
    rawCache.subModuleGroup.map((subModuleGroupItem) => {
      subModuleGroups.set(subModuleGroupItem.id, subModuleGroupItem);
    });
  }
}

function clear() {
  grades.clear();
  levels.clear();
  majors.clear();
  moduleGroups.clear();
  modules.clear();
  topics.clear();
  staffInfos.clear();
  schoolInfos.clear();
  classInfos.clear();
  studentInfos.clear();
  subModuleGroups.clear();
  majorGradeClassInfo.clear();
  majorGradeStudentInfo.clear();
  majorGradeModule.clear();
  studentClassInfo.clear();
  doWhenReadyFunctions.length = 0; //clear array
}

export const generateCache = (cache: legacyStorageTypes) => {
  ready = false;
  if (cache) rawCache = cache;
  clear();
  generateLevelCache();
  generateGradeCache();
  generateMajorCache();
  generateModuleGroupCache();
  generateModuleCache();
  generateSubModuleGroup();
  generateTopicCache();
  generateStaffInfoCache();
  generateSchoolInfoCache();
  generateClassInfoCache();
  generateStudentInfoCache();
  generateMajorGradeModule();
  ready = true;
  window.dispatchEvent(CACHE_LOADED_EVENT);
  while (doWhenReadyFunctions.length) {
    const doWhenReadyFunction =
      doWhenReadyFunctions.pop() as DoWhenReadyFunction;
    doWhenReadyFunction();
  }
};

export const doWhenReady = (doWhenReadyFunction: DoWhenReadyFunction) => {
  if (ready) {
    doWhenReadyFunction();
  } else {
    doWhenReadyFunctions.push(doWhenReadyFunction);
  }
};

export const getLevels = (): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  for (const level of levels.values()) {
    result.push(level);
  }
  return result;
};

export const getLevel = (id: number): Level | null => {
  if (id) {
    const level = levels.get(id) as Level;
    if (level) {
      return level;
    }
  }
  return null;
};

export const getGrades = (): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  for (const grade of grades.values()) {
    result.push(grade);
  }
  return result;
};

export const getGrade = (id: number): Grade | null => {
  if (id) {
    const grade = grades.get(id) as Grade;
    if (grade) {
      return grade;
    }
  }
  return null;
};

export const getGradesOfLevel = (levelId: number): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  if (!levelId) {
    return result;
  }
  const level = levels.get(levelId) as Level;
  if (!level || !level.grades) {
    return result;
  }
  for (const gradeId of level.grades) {
    const grade = grades.get(gradeId) as Grade;
    if (grade) {
      result.push(grade);
    }
  }
  return result;
};

export const getMajor = (id: number): Major | null => {
  if (id) {
    const major = majors.get(id) as Major;
    if (major) {
      return major;
    }
  }
  return null;
};

export const getMajorsOfGrade = (gradeId: number): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  if (!gradeId) {
    return result;
  }
  const grade = grades.get(gradeId) as Grade;
  if (!grade) {
    return result;
  }
  if (grade.majors && grade.majors.length) {
    for (const majorId of grade.majors) {
      const major = majors.get(majorId) as Major;
      if (major) {
        result.push(major);
      }
    }
  }
  return result;
};

export const getModuleGroup = (id: number): ModuleGroup | null => {
  if (id) {
    const moduleGroup = moduleGroups.get(id) as ModuleGroup;
    if (moduleGroup) {
      return moduleGroup;
    }
  }
  return null;
};

export const getModuleGroupsOf = (
  majorId: number,
  gradeId: number,
  isCommon: boolean
): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  if (!majorId || !gradeId) {
    return result;
  }
  const moduleIds = majorGradeModule.get(
    new MajorGrade(majorId, gradeId).getKeyOfMajorGrade()
  ) as number[];
  if (!moduleIds || !moduleIds.length) {
    return result;
  }
  for (const moduleId of moduleIds) {
    const res = modules.get(moduleId) as Module;
    if (res && res.isCommon === isCommon) {
      const moduleGroupId = getModuleGroupIdByModuleId(moduleId) as number;
      const idName: IdName = {
        id: moduleGroupId,
        name: getModuleGroupName(moduleGroupId) ?? ""
      };
      if (
        result.filter(
          (oldIdName) =>
            oldIdName.id === idName.id && oldIdName.name === idName.name
        ).length === 0
      ) {
        result.push(idName);
      }
    }
  }
  return result;
};

const getModuleGroupName = (id: number): string | null => {
  if (!id) {
    return null;
  }
  const moduleGroup = moduleGroups.get(id) as ModuleGroup;
  if (!moduleGroup) {
    return null;
  }
  return moduleGroup.name ?? "";
};

export const getModuleGroupIdByModuleId = (id: number): number | null => {
  if (!id) {
    return null;
  }
  const res = modules.get(id) as Module;
  if (!res) {
    return null;
  }
  return res.moduleGroupId;
};

export const getSubModuleGroup = (id: number): SubModuleGroup | null => {
  if (id) {
    const subModuleGroup = subModuleGroups.get(id) as SubModuleGroup;
    if (subModuleGroup) {
      return subModuleGroup;
    }
  }
  return null;
};

export const getSubModuleGroupsOf = (moduleGroupId: number): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  if (!moduleGroupId) {
    return result;
  }
  for (const subModuleGroup of subModuleGroups.values()) {
    if (subModuleGroup.moduleGroupId === moduleGroupId) {
      result.push(subModuleGroup);
    }
  }
  return result;
};

export const getModule = (id: number): Module | null => {
  if (id) {
    const res = modules.get(id) as Module;
    if (res) {
      return res;
    }
  }
  return null;
};

export const getModuleOfMajorGrade = (
  majorId: number,
  gradeId: number
): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  if (!majorId || !gradeId) {
    return result;
  }
  const moduleIds = majorGradeModule.get(
    new MajorGrade(majorId, gradeId).getKeyOfMajorGrade()
  ) as number[];
  if (!moduleIds || !moduleIds.length) {
    return result;
  }
  return moduleIds.map<IdName>((moduleId): IdName => {
    const res = getModule(moduleId) as Module;
    return { id: res.id, name: res.name };
  });
};

export const getModulesOf = (
  subModuleGroupId: number,
  majorId: number,
  gradeId: number,
  hasOnlineContents: boolean
): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  if (!subModuleGroupId || !majorId || !gradeId) {
    return result;
  }
  const modulesByMajorGrade: IdName[] = getModuleOfMajorGrade(majorId, gradeId);
  if (modulesByMajorGrade) {
    for (const moduleIdName of modulesByMajorGrade) {
      const res = modules.get(moduleIdName.id) as Module;
      if (
        res.subModuleGroupId === subModuleGroupId &&
        (!hasOnlineContents || res.hasOnlineContents)
      ) {
        result.push(moduleIdName);
      }
    }
  }
  return result;
};

export const getModulesByMajorGrade = (majorId?: number, gradeId?: number) => {
  if (!majorId || !gradeId) return [];

  return Array.from(
    new Set(
      getModuleOfMajorGrade(majorId, gradeId).map(
        (m) => getModule(m.id)?.subModuleGroupId
      )
    )
  ).flatMap((subModuleGroupId) =>
    getModulesOf(subModuleGroupId as number, majorId, gradeId, true)
  );
};

export const getTopic = (id: number): Topic | null => {
  if (id) {
    const topic = topics.get(id) as Topic;
    if (topic) {
      return topic;
    }
  }
  return null;
};

export const getChapters = (moduleId: number): IdName[] => {
  const result: IdName[] = new Array<IdName>();

  const cacheItem = modules.get(moduleId) as Module;
  if (!cacheItem || !cacheItem.chapters || !cacheItem.chapters.length) {
    return result;
  }
  for (const chapterId of cacheItem.chapters) {
    const topic = getTopic(chapterId) as Topic;
    result.push({ id: topic.id, name: topic.name });
  }
  return result;
};

export const getTopicChildren = (topicId: number): IdName[] => {
  const result = new Array<IdName>();
  if (!topicId) {
    return result;
  }
  const cacheItem = topics.get(topicId) as Topic;
  if (!cacheItem || !cacheItem.children || !cacheItem.children.length) {
    return result;
  }
  for (const childId of cacheItem.children) {
    const topic = getTopic(childId) as Topic;
    result.push({ id: topic.id, name: topic.name });
  }
  return result;
};

export const getClasses = (): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  for (const classInfo of classInfos.values()) {
    result.push(classInfo);
  }
  return result;
};

export const getGradeOfClass = (classId: number): number | null => {
  if (!classId) {
    return null;
  }
  for (const entry of majorGradeClassInfo) {
    if (entry[1].includes(classId)) {
      return MajorGrade.getMajorGradeFromKey(entry[0])?.grade as number;
    }
  }
  return null;
};

export const getMajorOfClass = (classId: number): number | null => {
  if (!classId) {
    return null;
  }
  for (const entry of majorGradeClassInfo) {
    if (entry[1].includes(classId)) {
      return MajorGrade.getMajorGradeFromKey(entry[0])?.majorId as number;
    }
  }
  return null;
};

export const getStudentsOfClass = (classId: number): IdName[] => {
  const result: IdName[] = new Array<IdName>();
  if (!classId) {
    return result;
  }
  const studentIds = studentClassInfo.get(classId) as number[];
  if (!studentIds || !studentIds.length) {
    return result;
  }
  for (const studentId of studentIds) {
    const student = studentInfos.get(studentId) as StudentInfo;
    result.push(student);
  }
  return result;
};

export const getStudent = (id: number): StudentInfo | null => {
  if (id) {
    const student = studentInfos.get(id) as StudentInfo;
    if (student) {
      return student;
    }
  }
  return null;
};
